/**
 * Order-free, bilingual (Russian + English) reader of a free-form task or event sentence.
 *
 * "Tomorrow at 14:00 prepare the report", "Подготовить отчёт завтра к 14:00", "В пятницу сделать
 * презентацию до 18:00", "Report review, high priority, Friday": every piece of metadata is a
 * separate recognizer that looks for its own phrase ANYWHERE in the sentence and cuts it out; what
 * is left is the title. The recognizers do not depend on each other's position, and none of them
 * guesses:
 *
 *  - a phrase is recognised only in a shape that is unmistakably metadata ("on Friday", "at 5pm",
 *    "25 October"); a bare number or a month / weekday word inside a title stays in the title;
 *  - what the task schema has no field for (a priority) is reported and its words stay in the title;
 *  - two readings, a contradiction or an impossible value (31 November, 25:00) is reported and the
 *    text is left as typed - nothing is corrected, no date or time is invented;
 *  - a single time or a date without a span is a DUE date / time (a task); a range or a duration
 *    is an event; a repeating sentence is an event and is read by the Russian series grammar of
 *    `parse.ts` (everything else stays in this file).
 *
 * No AI and no statistics: bounded patterns on text of at most 300 characters.
 */
import { addDays } from '@/shared/lib/calendar';

import {
  END,
  MONTHS_EN,
  MONTHS_GENITIVE,
  NUMBER,
  numberOf,
  START,
  WEEKDAY_STEMS,
  WEEKDAYS_EN,
} from './lexicon';
import {
  clock,
  isMidnightEnd,
  isoWeekdayIndex,
  makeDate,
  nextWeekday,
  parseEvent,
  re,
  Text,
  weekdayIndex,
  weekdayOfNextWeek,
  WD,
} from './parse';
import {
  MAX_INPUT_LENGTH,
  type ParseContext,
  type ParsedSentence,
  type ParseNote,
  type SentenceBoard,
  type SentenceKind,
} from './types';

export interface SentenceContext extends ParseContext {
  /** The boards a sentence may name ("on board Marketing"); none = a board cannot be read. */
  boards?: readonly { id: string; name: string }[];
}

// --- vocabulary --------------------------------------------------------------------------------

const MONTHS_RU = MONTHS_GENITIVE.join('|');
const MONTHS_EN_RE = MONTHS_EN.join('|');
const WD_EN = `(?:${WEEKDAYS_EN.join('|')})`;
const monthNumber = (name: string): number => {
  const ru = (MONTHS_GENITIVE as readonly string[]).indexOf(name);
  return ru >= 0 ? ru + 1 : (MONTHS_EN as readonly string[]).indexOf(name) + 1;
};
const weekdayOf = (word: string): number => {
  const en = (WEEKDAYS_EN as readonly string[]).indexOf(word);
  return en >= 0 ? en : weekdayIndex(word);
};

/** Prepositions that make the following date or time a DEADLINE ("by Friday", "до 18:00"). */
const DEADLINE_WORDS = new Set(['к', 'до', 'by', 'before', 'until', 'till', 'due']);

const MERIDIEM = '(?:a\\.m\\.|p\\.m\\.|am|pm)';

/** "Create a task", "add event", "создать задачу", "remind me to": not part of the title. */
const COMMAND_RU = `^\\s*(?:создай|создать|добавь|добавить|запланируй|запланировать|напомни|напомнить)(?:\\s+мне)?(?:\\s+(?:новую|новый|одну|одно))?(?:\\s+(задачу|дело|напоминание)|\\s+(событие|встречу|мероприятие))?`;
const COMMAND_EN = `^\\s*(?:please\\s+)?(?:(?:create|add|make|new|schedule)\\s+(?:(?:a|an|new)\\s+)?(?:(task|todo|to-do|reminder)|(event|meeting|appointment))|(?:remind\\s+me\\s+to|remember\\s+to))`;

/** Phrases that look like metadata but are not in the grammar: reported, never guessed. */
const UNSUPPORTED_RU = [
  'утром',
  'вечером',
  'днём',
  'днем',
  'ночью',
  'через',
  'следующ',
  'неделе',
  'выходных',
  'числа',
  'месяце',
  'года',
];
const UNSUPPORTED_EN = [
  'tonight',
  'morning',
  'afternoon',
  'evening',
  'weekend',
  'next\\s+week',
  'next\\s+month',
  'in\\s+(?:an?|\\d+)\\s+(?:hour|day|week|month)s?',
];
/** A repeat in the sentence: read by the series grammar when Russian, reported otherwise. */
const REPEAT_RU = new RegExp(
  `${START}(?:кажд[а-яё]*|ежедневн[а-яё]*|еженедельн[а-яё]*|ежемесячн[а-яё]*|ежегодн[а-яё]*|по\\s+будням|по\\s+выходным|по\\s+(?:${WEEKDAY_STEMS.join('|')})[а-яё]{0,4})${END}`,
  'u',
);
const REPEAT_EN = new RegExp(
  `${START}(?:every\\s+\\w+|daily|weekly|monthly|yearly|annually)${END}`,
  'u',
);
/** The task schema has no priority / status field: the words stay in the title, with a note. */
const PRIORITY = [
  `(?:high|low|medium|normal|urgent|critical)\\s+priority`,
  `priority\\s*[:=-]?\\s*(?:high|low|medium|normal|urgent|critical|p\\d)`,
  `(?:высок[а-яё]*|низк[а-яё]*|средн[а-яё]*|критичн[а-яё]*|срочн[а-яё]*)\\s+приоритет[а-яё]*`,
  `приоритет\\s*[:=-]?\\s*(?:высок[а-яё]*|низк[а-яё]*|средн[а-яё]*|срочн[а-яё]*)`,
  `asap`,
  `срочно`,
];

// --- small helpers -----------------------------------------------------------------------------

type Meridiem = 'am' | 'pm' | undefined;
const meridiemOf = (token: string | undefined): Meridiem =>
  token === undefined || token === '' ? undefined : token.startsWith('p') ? 'pm' : 'am';

/** `18:45`, `9`, `2pm`, with an optional part of the day: the time, or `null` if impossible. */
function clockOf(token: string, meridiem?: Meridiem, period?: string): string | null {
  const [hourText = '', minuteText] = token.split(':');
  let hour = Number(hourText);
  const minute = minuteText === undefined ? 0 : Number(minuteText);
  if (!Number.isInteger(hour) || !Number.isInteger(minute) || minute > 59) return null;
  if (meridiem) {
    if (hour < 1 || hour > 12) return null;
    hour = (hour % 12) + (meridiem === 'pm' ? 12 : 0);
  } else if (period === 'вечера' && hour >= 1 && hour < 12) hour += 12;
  else if (period === 'дня' && hour >= 1 && hour < 12) hour += 12;
  else if (period === 'ночи' && hour === 12) hour = 0;
  if (hour > 23) return null;
  return clock(hour, minute);
}

interface Dated {
  iso: string;
  year: 'given' | 'assumed' | 'n/a';
  deadline: boolean;
}

/** A leftover that is only separators and a stray preposition of a removed phrase. */
function titleOf(text: Text): string {
  let result = '';
  for (let index = 0; index < text.original.length; index++) {
    result += text.work[index] === '\u0001' ? ' ' : (text.original[index] ?? '');
  }
  return result
    .replace(/\s+/gu, ' ')
    .replace(/\s+([,;:.!?])/gu, '$1')
    .replace(/^[\s,;:–—-]+/u, '')
    .replace(/[\s,;:–—-]+$/u, '')
    .trim();
}

/** The boards whose name (longest first) starts at `from` in the lower-cased work text. */
function boardAt(
  work: string,
  from: number,
  boards: readonly { id: string; name: string }[],
): { board: { id: string; name: string }; end: number }[] {
  const found: { board: { id: string; name: string }; end: number }[] = [];
  for (const board of [...boards].sort((a, b) => b.name.length - a.name.length)) {
    const name = board.name.toLowerCase().trim();
    if (!name) continue;
    if (work.startsWith(name, from)) {
      const after = work[from + name.length] ?? '';
      if (after === '' || !/[\p{L}\p{N}]/u.test(after))
        found.push({ board, end: from + name.length });
    }
  }
  return found;
}

// --- the reader --------------------------------------------------------------------------------

export function parseSentence(input: string, context: SentenceContext): ParsedSentence {
  const notes: ParseNote[] = [];
  const note = (kind: ParseNote['kind'], code: string, params?: ParseNote['params']) => {
    notes.push({ kind, code, ...(params && { params }) });
  };
  const blank = (): ParsedSentence => ({
    input,
    title: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    allDay: false,
    durationMinutes: null,
    recurrence: null,
    notes,
    complete: false,
    kind: 'deadline',
    board: null,
    cue: null,
  });
  if (input.length > MAX_INPUT_LENGTH) {
    note('invalid', 'tooLong', { max: MAX_INPUT_LENGTH });
    return blank();
  }
  const today = context.today;
  const year = Number(today.slice(0, 4));
  const text = new Text(input);

  // --- "create a task ...": a command, not the title --------------------------------------------
  let cue: SentenceKind | null = null;
  const command = text.take(re(COMMAND_RU)) ?? text.take(re(COMMAND_EN));
  if (command) {
    if (command[1]) cue = 'deadline';
    else if (command[2]) cue = 'event';
    else if (/remind|remember|напомни/u.test(command[0])) cue = 'deadline';
  }

  // --- the board ("on board Marketing", "на доске Маркетинг", "#Marketing") ---------------------
  let board: SentenceBoard | null = null;
  const boards = context.boards ?? [];
  const strict = text.find(
    re(
      `${START}(?:on|to|in|for)\\s+(?:the\\s+)?board${END}|${START}(?:на|в|для)\\s+доск[а-яё]{1,2}${END}`,
    ),
  );
  const loose = strict ? null : text.find(re(`${START}(?:board|доск[а-яё]{1,2})${END}`));
  const hash = text.find(re('(?<![\\p{L}\\p{N}])#'));
  const anchor = strict ?? hash ?? loose;
  if (anchor) {
    let from = anchor.index + anchor[0].length;
    while (text.work[from] === ' ' || text.work[from] === ':') from += 1;
    if (/["«“]/u.test(text.work[from] ?? '')) from += 1; // a quoted name
    const candidates = boardAt(text.work, from, boards);
    const best = candidates[0];
    if (best && candidates.filter((c) => c.end === best.end).length === 1) {
      let end = best.end;
      if (/["»”]/u.test(text.work[end] ?? '')) end += 1;
      text.cut(anchor.index, end);
      board = { id: best.board.id, name: best.board.name };
    } else if (candidates.length > 1) {
      note('ambiguous', 'boardAmbiguous');
    } else if (strict ?? hash) {
      // a board is clearly meant ("on board X", "#X") but no board of that name exists
      note('unsupported', 'boardUnknown', {
        word: text.work.slice(anchor.index, anchor.index + 24).trim(),
      });
    }
  }

  // --- a repeating sentence is an event, read by the series grammar ------------------------------
  if (REPEAT_RU.test(text.work)) {
    const masked = Array.from(text.original, (char, index) =>
      text.work[index] === '\u0001' ? ' ' : char,
    ).join('');
    const series = parseEvent(masked, { today });
    return {
      ...series,
      input,
      kind: 'event',
      board,
      cue,
      notes: [...notes, ...series.notes],
      complete: series.complete && notes.every((n) => n.kind === 'assumed'),
    };
  }
  if (REPEAT_EN.test(text.work))
    note('unsupported', 'unsupportedRepeat', { word: REPEAT_EN.exec(text.work)?.[0] ?? '' });

  // --- dates: anywhere, in any order ------------------------------------------------------------
  const found: Dated[] = [];
  const invalidDate = (value: string) => {
    note('invalid', 'invalidDate', { value });
  };
  // Returns whether the phrase was a real date: an impossible one (31 November) is reported and
  // stays in the text, as typed.
  const explicit = (
    day: number,
    month: number,
    given: number | null,
    deadline: boolean,
  ): boolean => {
    if (given !== null) {
      const iso = makeDate(given, month, day);
      if (iso) found.push({ iso, year: 'given', deadline });
      else invalidDate(`${String(day)}.${String(month)}.${String(given)}`);
      return iso !== null;
    }
    const thisYear = makeDate(year, month, day);
    if (!thisYear) {
      invalidDate(`${String(day)}.${String(month)}`);
      return false;
    }
    if (thisYear < today) {
      const next = makeDate(year + 1, month, day);
      if (next) found.push({ iso: next, year: 'assumed', deadline });
      return next !== null;
    }
    found.push({ iso: thisYear, year: 'n/a', deadline });
    return true;
  };
  const lead = '(?:(на|к|до|by|before|until|till|due|for|on|в|во)\\s+)?';
  const patterns: { source: string; read: (m: RegExpExecArray) => boolean }[] = [
    {
      source: `${START}(\\d{4})-(\\d{2})-(\\d{2})${END}`,
      read: (m) => explicit(Number(m[3]), Number(m[2]), Number(m[1]), false),
    },
    {
      source: `${START}${lead}(\\d{1,2})(?:st|nd|rd|th)?\\s+(?:of\\s+)?(${MONTHS_RU}|${MONTHS_EN_RE})(?:\\s*,?\\s*(\\d{4}))?${END}`,
      read: (m) =>
        explicit(
          Number(m[2]),
          monthNumber(m[3] ?? ''),
          m[4] ? Number(m[4]) : null,
          DEADLINE_WORDS.has(m[1] ?? ''),
        ),
    },
    {
      source: `${START}${lead}(${MONTHS_EN_RE})\\s+(\\d{1,2})(?:st|nd|rd|th)?(?![\\d:])(?:\\s*,?\\s*(\\d{4})(?![\\d:]))?${END}`,
      read: (m) =>
        explicit(
          Number(m[3]),
          monthNumber(m[2] ?? ''),
          m[4] ? Number(m[4]) : null,
          DEADLINE_WORDS.has(m[1] ?? ''),
        ),
    },
    {
      source: `${START}${lead}(\\d{1,2})\\.(0?[1-9]|1[0-2])(?:\\.(\\d{4}))?${END}`,
      read: (m) =>
        explicit(
          Number(m[2]),
          Number(m[3]),
          m[4] ? Number(m[4]) : null,
          DEADLINE_WORDS.has(m[1] ?? ''),
        ),
    },
  ];
  for (const { source, read } of patterns) {
    for (let step = 0; step < 3; step++) {
      const m = text.find(re(source));
      if (!m || !read(m)) break;
      text.consume(m);
    }
  }
  const relative = text.take(
    re(
      `${START}(?:(на|к|до|by|before|until|till|for|on)\\s+)?(?:the\\s+)?(day\\s+after\\s+tomorrow|послезавтра|tomorrow|завтра|today|сегодня)${END}`,
    ),
  );
  if (relative) {
    const word = relative[2] ?? '';
    const offset = /after|после/u.test(word) ? 2 : /tomorrow|завтра/u.test(word) ? 1 : 0;
    found.push({
      iso: addDays(today, offset),
      year: 'n/a',
      deadline: DEADLINE_WORDS.has(relative[1] ?? ''),
    });
  }
  // a weekday: after a preposition ("on Friday", "в пятницу", "на понедельник", "к пятнице") ...
  let weekday: number | null = null;
  let weekdayDate = '';
  let weekdayDeadline = false;
  const spelled =
    text.take(re(`${START}(в|во|на|к|до)\\s+(?:(следующ[а-яё]{2,3})\\s+)?(${WD})${END}`)) ??
    text.take(
      re(`${START}(?:(on|by|before|until|till|this|next)\\s+)(?:(next)\\s+)?(${WD_EN})${END}`),
    );
  if (spelled) {
    const nextWeek =
      spelled[2] === 'next' || /^следующ/u.test(spelled[2] ?? '') || spelled[1] === 'next';
    weekday = weekdayOf(spelled[3] ?? '');
    weekdayDate = nextWeek ? weekdayOfNextWeek(today, weekday) : nextWeekday(today, weekday);
    weekdayDeadline = DEADLINE_WORDS.has(spelled[1] ?? '');
  }

  // --- times: a range, a deadline time, a start time, a bare time ----------------------------------
  let startTime = '';
  let endTime = '';
  let explicitEnd = false;
  let midnightEnd = false;
  const flags = { deadlineTime: false };
  const invalidTime = (value: string) => {
    note('invalid', 'invalidTime', { value });
  };
  const side = '(\\d{1,2}(?::\\d{2})?)\\s*(a\\.m\\.|p\\.m\\.|am|pm)?';
  const withPrefix = text.find(
    re(
      `${START}(?:с|со|from|between)\\s+${side}\\s*(?:до|по|to|until|till|and|[-–—])\\s*${side}${END}`,
    ),
  );
  const dashed = withPrefix ? null : text.find(re(`${START}${side}\\s*[-–—]\\s*${side}${END}`));
  // without a leading word ("from") a dash needs both sides to be clearly times: `9:00`, `9am`
  const clear = (token: string | undefined, mer: string | undefined) =>
    (token ?? '').includes(':') || Boolean(mer);
  const range =
    withPrefix ??
    (dashed && clear(dashed[1], dashed[2]) && clear(dashed[3], dashed[4]) ? dashed : null);
  if (range) {
    // the two ends: the first takes the second one's am / pm when it has none ("from 2 to 4pm")
    const secondMer = meridiemOf(range[4]);
    const firstMer =
      meridiemOf(range[2]) ??
      (secondMer && Number((range[1] ?? '').split(':')[0]) <= Number((range[3] ?? '').split(':')[0])
        ? secondMer
        : undefined);
    const from = clockOf(range[1] ?? '', firstMer);
    midnightEnd = isMidnightEnd(range[3] ?? '') && !range[4];
    const to = midnightEnd ? '00:00' : clockOf(range[3] ?? '', secondMer);
    if (!(from && to)) midnightEnd = false;
    if (from && to) {
      text.consume(range);
      [startTime, endTime, explicitEnd] = [from, to, true];
    } else {
      invalidTime(!from ? (range[1] ?? '') : (range[3] ?? ''));
    }
  }
  const setTime = (value: string | null, deadline: boolean) => {
    if (!value) return;
    if (startTime && value !== startTime) note('ambiguous', 'twoTimes');
    else {
      startTime = value;
      flags.deadlineTime = flags.deadlineTime || deadline;
    }
  };
  // "до 18:00", "к 14:00", "by 6pm", "before 17:30": a DEADLINE time
  const marker = text.find(
    re(
      `${START}(до|к|by|before|until|till|due(?:\\s+by)?)\\s+(\\d{1,2}:\\d{2}|\\d{1,2}\\s*${MERIDIEM}|\\d{1,2}\\s+(?:утра|дня|вечера|ночи|часов|часа|час))${END}`,
    ),
  );
  if (marker) {
    const body = marker[2] ?? '';
    const period = /(утра|дня|вечера|ночи)$/u.exec(body)?.[1];
    const parts = /^(\d{1,2}(?::\d{2})?)\s*(a\.m\.|p\.m\.|am|pm)?/u.exec(body);
    const value = clockOf(parts?.[1] ?? '', meridiemOf(parts?.[2]), period);
    if (value) {
      text.consume(marker);
      setTime(value, true);
    } else {
      invalidTime(parts?.[1] ?? body);
    }
  }
  // "в 14:00", "at 5pm", "в 9 утра", "@ 17:30"
  const at =
    text.find(re(`${START}(?:в|во|at|@)\\s*(\\d{1,2}(?::\\d{2})?)\\s*(${MERIDIEM})${END}`)) ??
    text.find(re(`${START}(?:в|во|at|@)\\s*(\\d{1,2}:\\d{2})${END}`)) ??
    text.find(re(`${START}(?:в|во)\\s+(\\d{1,2})\\s+(утра|дня|вечера|ночи|часов|часа|час)${END}`));
  if (at) {
    const period = ['утра', 'дня', 'вечера', 'ночи'].includes(at[2] ?? '') ? at[2] : undefined;
    const value = clockOf(
      at[1] ?? '',
      /^(?:a|p)/u.test(at[2] ?? '') ? meridiemOf(at[2]) : undefined,
      period,
    );
    if (value) {
      text.consume(at);
      setTime(value, false);
    } else {
      invalidTime(at[1] ?? '');
    }
  }
  // a time on its own: unmistakable only as `14:00` or `2pm`
  for (let step = 0; step < 2; step++) {
    const bare =
      text.find(re(`${START}(\\d{1,2}:\\d{2})${END}`)) ??
      text.find(re(`${START}(\\d{1,2}(?::\\d{2})?)\\s*(${MERIDIEM})${END}`));
    if (!bare) break;
    const value = clockOf(bare[1] ?? '', meridiemOf(bare[2]));
    if (!value) {
      invalidTime(bare[0]);
      break;
    }
    text.consume(bare);
    setTime(value, false);
  }
  // an hour with a preposition but nothing that says which hour of the day ("at 5")
  const vague = text.find(re(`${START}(?:в|во|at|by|до|к)\\s+(\\d{1,2})(?![\\d:.])${END}`));
  if (vague) note('ambiguous', 'ambiguousHour', { value: vague[0] });

  // --- a duration ("на 2 часа", "for 45 minutes") -------------------------------------------------
  let duration: number | null = null;
  const UNIT = `(?:(?:\\d{1,3}|${NUMBER.slice(1, -1).split('|').slice(1).join('|')})\\s+)?(?:час(?:а|ов)?|минут[уыа]?)`;
  const durationRu = text.find(
    re(`${START}на\\s+(полчаса|полтора\\s+часа|полторы\\s+часа|(?:${UNIT})(?:\\s+${UNIT})?)${END}`),
  );
  const durationEn = text.find(
    re(
      `${START}for\\s+(half\\s+an?\\s+hour|an?\\s+hour|\\d{1,3}\\s*(?:hours?|hrs?|h)(?:\\s+\\d{1,3}\\s*(?:minutes?|mins?|m))?|\\d{1,3}\\s*(?:minutes?|mins?|m))${END}`,
    ),
  );
  if (durationRu) {
    text.consume(durationRu);
    const body = durationRu[1] ?? '';
    if (body === 'полчаса') duration = 30;
    else if (body.startsWith('полтор')) duration = 90;
    else {
      duration = 0;
      const unit = re(`(?:(\\d{1,3}|${NUMBER.slice(1, -1)})\\s+)?(час|минут)`, 'gu');
      for (let step = 0; step < 2; step++) {
        const piece = unit.exec(body);
        if (!piece) break;
        const amount = piece[1] ? numberOf(piece[1]) : 1;
        duration += piece[2] === 'час' ? amount * 60 : amount;
      }
    }
  } else if (durationEn) {
    text.consume(durationEn);
    const body = durationEn[1] ?? '';
    if (body.startsWith('half')) duration = 30;
    else if (/^an?\s+hour/u.test(body)) duration = 60;
    else {
      duration = 0;
      const unit = /(\d{1,3})\s*(hours?|hrs?|h|minutes?|mins?|m)(?![a-z])/gu;
      for (let step = 0; step < 2; step++) {
        const piece = unit.exec(body);
        if (!piece) break;
        duration += /^h/u.test(piece[2] ?? '') ? Number(piece[1]) * 60 : Number(piece[1]);
      }
    }
  }
  if (duration !== null && (duration < 1 || duration > 1440)) {
    note('invalid', 'invalidDuration');
    duration = null;
  }

  // a weekday on its own: only at the end of the sentence or between separators, where nothing
  // else can be meant ("Report review, high priority, Friday"); inside a title it stays a word
  if (weekday === null) {
    const bareDay = text.take(
      re(
        `(?<=^|[,;:\\u0001]\\s*)(${WD}|${WD_EN})(?=\\s*(?:[,;:\\u0001]|$))|(?<=\\s)(${WD}|${WD_EN})(?=\\s*$)`,
      ),
    );
    if (bareDay) {
      weekday = weekdayOf(bareDay[1] ?? bareDay[2] ?? '');
      weekdayDate = nextWeekday(today, weekday);
    } else if (found.length === 0) {
      const maybe = text.find(
        re(`${START}(${WD_EN}|(?:${WEEKDAY_STEMS.join('|')})[а-яё]{0,4})${END}`),
      );
      if (maybe) note('ambiguous', 'bareWeekday', { word: maybe[0] });
    }
  }

  // --- what the schema cannot hold, and what is outside the grammar ----------------------------
  for (const source of PRIORITY) {
    const priority = text.find(re(`${START}${source}${END}`));
    if (priority) {
      note('unsupported', 'priorityNotSupported', { word: priority[0] });
      break;
    }
  }
  for (const word of [...UNSUPPORTED_RU.map((w) => `${w}[а-яё]*`), ...UNSUPPORTED_EN]) {
    const left = text.find(re(`${START}${word}${END}`));
    if (left) {
      note('unsupported', 'unsupportedPhrase', { word: left[0] });
      break;
    }
  }

  // --- the date ----------------------------------------------------------------------------------
  // A weekday names the date only when nothing else does; next to a date it is a check.
  if (weekdayDate && found.length === 0) {
    found.push({ iso: weekdayDate, year: 'n/a', deadline: weekdayDeadline });
  }
  const distinct = [...new Set(found.map((item) => item.iso))];
  let startDate = '';
  if (distinct.length > 1) {
    note('ambiguous', 'ambiguousDates');
  } else if (distinct.length === 1) {
    startDate = distinct[0] ?? '';
    if (found.some((item) => item.year === 'assumed'))
      note('assumed', 'assumedYear', { date: startDate });
    if (weekday !== null && isoWeekdayIndex(startDate) !== weekday)
      note('ambiguous', 'weekdayMismatch');
  } else if (!notes.some((n) => n.code === 'invalidDate')) {
    note('missing', 'missingDate');
  }
  const dateDeadline = found.some((item) => item.deadline);

  // --- the end, the kind --------------------------------------------------------------------------
  let endDate = '';
  if (startTime && duration !== null && !explicitEnd) {
    const [h = 0, mi = 0] = startTime.split(':').map(Number);
    const total = h * 60 + mi + duration;
    endTime = clock(Math.floor(total / 60) % 24, total % 60);
    endDate = startDate ? addDays(startDate, Math.floor(total / 1440)) : '';
    if (total >= 1440) note('assumed', 'assumedOvernight');
  } else if (explicitEnd && duration !== null) {
    note('ambiguous', 'durationConflict');
  }
  if (startTime && endTime && explicitEnd) {
    if (midnightEnd) {
      endDate = startDate ? addDays(startDate, 1) : '';
      note('assumed', 'assumedOvernight');
    } else if (endTime < startTime) {
      endDate = startDate ? addDays(startDate, 1) : '';
      note('assumed', 'assumedOvernight');
    } else if (endTime === startTime) {
      note('invalid', 'emptyInterval');
      endTime = '';
    } else {
      endDate = startDate;
    }
  } else if (startTime && endTime && !endDate) {
    endDate = startDate;
  }
  if (!startTime && duration !== null) note('missing', 'missingStart');

  // An interval needs a span; anything else is a due date / time (a task), unless the sentence
  // says "create an event".
  const span = endTime !== '' || explicitEnd || (duration !== null && startTime !== '');
  const kind: SentenceKind = span || cue === 'event' ? 'event' : 'deadline';
  // "by 18:00" next to a range, or "create a task" with an interval: two readings, none picked
  if (span && (flags.deadlineTime || dateDeadline || cue === 'deadline')) {
    note('ambiguous', 'deadlineWithSpan');
  }
  let allDay = false;
  if (kind === 'event') {
    if (startTime && !endTime && !notes.some((n) => n.code === 'invalidDuration')) {
      note('missing', 'missingEnd');
    }
    allDay = startDate !== '' && !startTime && !endTime;
    if (allDay) note('assumed', 'assumedAllDay');
  } else if (startDate !== '') {
    note('assumed', 'assumedDue', startTime ? { value: startTime } : undefined);
  }
  if (!startDate) endDate = '';

  const title = titleOf(text);
  if (!title) note('missing', 'missingTitle');

  const unique = notes.filter(
    (item, index) =>
      notes.findIndex(
        (other) =>
          other.code === item.code && JSON.stringify(other.params) === JSON.stringify(item.params),
      ) === index,
  );
  notes.splice(0, notes.length, ...unique);
  const blocking = notes.some((n) => n.kind !== 'assumed');
  return {
    input,
    title,
    startDate,
    startTime,
    endDate,
    endTime,
    allDay,
    durationMinutes: duration,
    recurrence: null,
    notes,
    complete: !blocking && startDate !== '' && title !== '',
    kind,
    board,
    cue,
  };
}
